import {
    select as D3select,
    max as D3max,
    format as D3format
} from 'd3';

/*
    radar Graph (used for ML data)
    generally, your data should consist of background layers and data layers.

    expects: {
        id: 'once',
        // layers
        data: [
            {
                color: background color for the layer
                label: bool, show axis labels. only outermost layer should have this
                points: bool, show data points for this layer. only data layer should have this
                range: { min, max}, range for values across all-axes
                axis: [
                    { axis: axis label, value: data value }
                ]
            }
        ]
    }
*/
const D3Radar = function () {
    let size = 500;
    let theme;
    let padding = 60;

    const radians = 2 * Math.PI;
    const pointSize = 4;

    function my (selection) {
        if (!selection) return;
        selection.each(function (data, i) {
            // add the parent once
            let g = D3select(this).selectAll('.radar').data(['once']);
            g = g.enter()
                .append('g').attr('class', 'radar')
                .attr('transform', `translate(${padding * 2}, ${padding / 2.5})`)
                .merge(g);

            const max = D3max(data.data, d => D3max(d.axes, o => o.value));
            const rsize = size - padding;

            // figure out polygon coordinates
            (data.data || []).forEach(group => {
                const l = group.axes.length;
                group.axes.forEach((d, i) => {
                    const v = parseFloat(Math.max(d.value, 0));
                    d.coordinates = {
                        x: rsize / 2 * (1 - (v / max) * Math.sin(i * radians / l)),
                        y: rsize / 2 * (1 - (v / max) * Math.cos(i * radians / l))
                    };
                });
            });

            // flattened data points
            const datapoints = (data.data || [])
                .filter(d => d.points)
                .reduce((a, c) => ([
                    ...a,
                    ...c.axes.map(a => ({ ...a, color: c.color }))]
                ), []);

            // layers
            const layers = g.selectAll('.polygon')
                .data(data.data);

            const layersEnter = layers.enter()
                .append('svg:polygon')
                .classed('polygon', true);

            layers.exit().remove();

            layersEnter.merge(layers)
                .attr('points', function (d) {
                    return (d.axes || []).reduce((a, c) =>
                        `${a}${c.coordinates.x},${c.coordinates.y} `, '');
                })
                .attr('stroke', function (d) { return d.color })
                .attr('fill', function (d) { return d.color })
                .attr('fill-opacity', 0.3)
                .attr('stroke-opacity', 1)
                .on('mouseover', function (e, d) {
                    // fade all other polygons out
                    g.selectAll('.polygon')
                        .transition(250)
                        .attr('fill-opacity', 0.1)
                        .attr('stroke-opacity', 0.1);
                    // focus on active polygon
                    D3select(this)
                        .transition(250)
                        .attr('fill-opacity', 0.7)
                        .attr('stroke-opacity', 1);
                    // if on background, fade all but points in range
                    g.selectAll('.point')
                        .transition(250)
                        .attr('fill-opacity', function (pd) {
                            return +pd.value >= d.range.min && +pd.value <= d.range.max
                                ? 1 : 0.1;
                        });
                    g.selectAll('.layers-label')
                        .transition(250)
                        .attr('opacity', function (pd) {
                            return !d.points && +pd.value === d.value
                                ? 1 : 0;
                        });
                    // show data labels in range
                    g.selectAll('.values-label')
                        .transition(250)
                        .attr('opacity', function (pd) {
                            return +pd.value >= d.range.min && +pd.value <= d.range.max
                                ? 1 : 0;
                        });
                    // map of axes in range
                    const inrange = datapoints.reduce((a, c) => ({
                        ...a,
                        [c.group]: a.group || (+c.value >= d.range.min && +c.value <= d.range.max)
                    }), {});
                    // show axis labels in range
                    g.selectAll('.axis-label')
                        .transition(250)
                        .attr('opacity', function (pd) {
                            return inrange[pd.axis] ? 1 : 0;
                        });
                })
                .on('mouseout', function (e) {
                    // put everything back where we found it
                    g.selectAll('.polygon')
                        .transition(250)
                        .attr('fill-opacity', 0.3)
                        .attr('stroke-opacity', 1);
                    g.selectAll('.point')
                        .transition(250)
                        .attr('fill', function (d) { return d.color })
                        .attr('fill-opacity', 1);
                    g.selectAll('.layers-label')
                        .transition(250)
                        .attr('opacity', 1);
                    g.selectAll('.values-label')
                        .transition(250)
                        .attr('opacity', 0);
                    g.selectAll('.axis-label')
                        .transition(250)
                        .attr('opacity', 1);
                });

            // layer labels
            const layerLabels = (data.data || [])
                .filter(d => !d.points)
                .map(d =>
                    d.axes.reduce((a, c) => !a.coordinates
                        ? { ...c, color: d.color }
                        : c.coordinates.y < a.coordinates.y ? { ...c, color: d.color }
                            : a, {})
                );

            const layersl = g.selectAll('.layers-label')
                .data(layerLabels);

            const layerslEnter = layersl.enter()
                .append('svg:text')
                .classed('layers-label', true);

            layersl.exit().remove();

            layerslEnter.merge(layersl)
                .text(function (d) { return D3format(',.1f')(+d.value) })
                .attr('text-anchor', 'middle')
                .attr('x', function (d) { return d.coordinates.x })
                .attr('y', function (d) { return d.coordinates.y + 20 });

            // points
            const points = g.selectAll('.point')
                .data(datapoints);

            const pointsEnter = points.enter()
                .append('svg:circle')
                .classed('point', true);

            points.exit().remove();

            pointsEnter.merge(points)
                .attr('r', pointSize)
                .attr('cx', function (d) { return d.coordinates.x })
                .attr('cy', function (d) { return d.coordinates.y })
                .attr('fill', function (d) { return d.color })
                .attr('fill-opacity', 1);

            // value labels (shown on hover)
            const valuesl = g.selectAll('.values-label')
                .data(datapoints);

            const valueslEnter = valuesl.enter()
                .append('svg:text')
                .classed('values-label', true);

            valuesl.exit().remove();

            valueslEnter.merge(valuesl)
                .text(function (d) { return D3format(',.1f')(+d.value) })
                .attr('text-anchor', 'middle')
                .attr('x', function (d) { return d.coordinates.x })
                .attr('y', function (d) { return d.coordinates.y + 20 })
                .attr('opacity', 0);

            // axes labels
            const axislabels = (data.data || [])
                .filter(d => d.label)
                .reduce((a, c) => ([
                    ...a,
                    ...c.axes.map(a => ({ ...a, color: c.color }))]
                ), []);

            const axesl = g.selectAll('.axis-label')
                .data(axislabels);

            const axeslEnter = axesl.enter()
                .append('svg:text')
                .classed('axis-label', true);

            axesl.exit().remove();

            axeslEnter.merge(axesl)
                .text(function (d) { return d.axis })
                .attr('text-anchor', function (d) {
                    return d.coordinates.x === rsize / 2 ? 'middle'
                        : d.coordinates.x > rsize / 2 ? 'start'
                            : 'end';
                })
                .attr('alignment-baseline', function (d) {
                    return d.coordinates.y === rsize / 2 ? 'middle'
                        : d.coordinates.y > rsize / 2 ? 'hanging'
                            : 'baseline';
                })
                .attr('x', function (d, i) {
                    return d.coordinates.x +
                        (d.coordinates.x > rsize / 2 ? 8 : -8);
                })
                .attr('y', function (d, i) {
                    return d.coordinates.y +
                        (d.coordinates.y > rsize / 2 ? 8 : -8);
                }).on('mouseover', function (e, d) {
                    // fade all other axis labels out
                    g.selectAll('.axis-label')
                        .transition(250)
                        .attr('opacity', 0.1);
                    // focus on active polygon
                    D3select(this)
                        .transition(250)
                        .attr('opacity', 1);
                    // fade all but points in axis
                    g.selectAll('.point')
                        .transition(250)
                        .attr('fill-opacity', function (pd) {
                            return pd.group === d.axis ? 1 : 0.1;
                        });
                    // show data labels in axis
                    g.selectAll('.values-label')
                        .transition(250)
                        .attr('opacity', function (pd) {
                            return pd.group === d.axis ? 1 : 0.1;
                        });

                    const values = datapoints.filter(p => p.group === d.axis);

                    // show layer in axis
                    g.selectAll('.polygon')
                        .transition(250)
                        .attr('fill-opacity', function (d) {
                            return values.some(v => v.value >= d.range.min &&
                                v.value <= d.range.max)
                                ? 0.3 : 0;
                        })
                        .attr('stroke-opacity', function (d) {
                            return values.some(v => v.value >= d.range.min &&
                                v.value <= d.range.max)
                                ? 1 : 0;
                        });

                    // fade all layer labels
                    g.selectAll('.layers-label')
                        .transition(250)
                        .attr('opacity', 0.1);
                })
                .on('mouseout', function (e) {
                    // put everything back where we found it
                    g.selectAll('.axis-label')
                        .transition(250)
                        .attr('opacity', 1);
                    g.selectAll('.point')
                        .transition(250)
                        .attr('fill', function (d) { return d.color })
                        .attr('fill-opacity', 1);
                    g.selectAll('.layers-label')
                        .transition(250)
                        .attr('opacity', 1);
                    g.selectAll('.values-label')
                        .transition(250)
                        .attr('opacity', 0);
                    g.selectAll('.polygon')
                        .transition(250)
                        .attr('fill-opacity', 0.3)
                        .attr('stroke-opacity', 1);
                });
        });
    }

    my.size = function (v) {
        if (!arguments.length) return size;
        size = v;
        return my;
    };

    my.padding = function (v) {
        if (!arguments.length) return padding;
        padding = v;
        return my;
    };

    my.theme = function (v) {
        if (!arguments.length) return theme;
        theme = v || theme;
        return my;
    };

    return my;
};

export default D3Radar;
